import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShieldLogo } from "../components/landing/Logo";
import {
  Eye, Upload, Camera, AlertTriangle, CheckCircle2, Loader2,
  ChefHat, Utensils, Thermometer, Users, Trash2, Shield,
  ArrowLeft, Volume2, Image as ImageIcon, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Analysis types for trial users
const ANALYSIS_TYPES = {
  plating: {
    name: "Plating Check",
    description: "Analyze dish presentation",
    icon: ChefHat,
    color: "gold"
  },
  inventory: {
    name: "Inventory Scan",
    description: "Check stock levels",
    icon: Utensils,
    color: "blue"
  },
  spoilage: {
    name: "Freshness Check",
    description: "Detect food safety issues",
    icon: AlertTriangle,
    color: "red"
  },
  equipment: {
    name: "Equipment Monitor",
    description: "Check operational status",
    icon: Thermometer,
    color: "cyan"
  }
};

const SEVERITY_COLORS = {
  CRITICAL: "bg-red-500/20 border-red-500/50 text-red-400",
  WARNING: "bg-amber-500/20 border-amber-500/50 text-amber-400",
  INFO: "bg-blue-500/20 border-blue-500/50 text-blue-400"
};

export default function TrialVision() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [selectedType, setSelectedType] = useState("plating");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const trialToken = sessionStorage.getItem("argus_trial_token");

  useEffect(() => {
    if (!trialToken) {
      navigate("/trial/login");
    }
  }, [trialToken, navigate]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
    setResults(null);
  };

  const handleAnalyze = async () => {
    if (!selectedImage) {
      toast.error("Please select an image first");
      return;
    }

    setAnalyzing(true);
    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.readAsDataURL(selectedImage);
      
      reader.onload = async () => {
        // Strip data URL prefix — backend expects pure base64 only
        const raw = reader.result;
        const base64Image = raw.includes(",") ? raw.split(",")[1] : raw;
        
        try {
          const res = await axios.post(`${API}/camera/analyze-frame`, {
            frame_base64: base64Image,
            analysis_type: selectedType,
            device_type: "webcam",
            device_id: "trial-web-upload"
          }, {
            headers: { Authorization: `Bearer ${trialToken}` }
          });

          setResults(res.data);
          toast.success("Analysis complete!");

          // Update trial progress (belt-and-suspenders; server updates on success)
          try {
            await axios.post(`${API}/trial/update-progress?mission=apollo_tested&completed=true`, null, {
              headers: { Authorization: `Bearer ${trialToken}` }
            });
          } catch (e) {
            // Non-fatal
          }
        } catch (err) {
          const detail = err.response?.data?.detail;
          // OD-3: Handle structured entitlement exhaustion response
          if (detail && typeof detail === "object" && detail.code === "TRIAL_ENTITLEMENT_EXHAUSTED") {
            toast.error(`Apollo trial limit reached (${detail.limit} analyses). Upgrade to Founding Member for unlimited access.`);
          } else {
            toast.error(typeof detail === "string" ? detail : "Analysis failed. Please try again.");
          }
        } finally {
          setAnalyzing(false);
        }
      };
    } catch (err) {
      toast.error("Failed to process image");
      setAnalyzing(false);
    }
  };

  const handleSpeak = async (text) => {
    if (isSpeaking) return;
    
    setIsSpeaking(true);
    try {
      const res = await axios.post(`${API}/sop/tts`, {
        text,
        voice: "onyx"
      }, {
        headers: { Authorization: `Bearer ${trialToken}` },
        responseType: "blob"
      });

      const audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      audio.onended = () => setIsSpeaking(false);
      audio.play();
    } catch (err) {
      toast.error("Failed to generate audio");
      setIsSpeaking(false);
    }
  };

  const TypeConfig = ANALYSIS_TYPES[selectedType];
  const TypeIcon = TypeConfig?.icon || Eye;

  return (
    <div className="min-h-screen bg-[#040F49]">
      {/* Header */}
      <header className="border-b border-gold/10 bg-[#040F49]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/trial/dashboard"
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back to Dashboard</span>
              </Link>
            </div>
            
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-blue-400" />
              <span className="font-inter font-bold text-white">Supervisor Apollo</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Privacy Notice */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-lg p-4 border border-lime/30 bg-lime/5 mb-6"
        >
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-lime shrink-0 mt-0.5" />
            <div>
              <h4 className="font-inter font-bold text-lime text-sm">Privacy-First Processing</h4>
              <p className="text-xs text-slate-400 mt-1">
                Images are analyzed and <span className="text-lime font-semibold">deleted instantly</span>. 
                No footage is ever stored. Your data stays private.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Upload Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-inter font-bold text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-gold" />
              Upload Image
            </h2>

            {/* Analysis Type Selector */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Analysis Type</label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="bg-indigo-700/30 border-gold/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ANALYSIS_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <config.icon className={`w-4 h-4 text-${config.color}-400`} />
                        <span>{config.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">{TypeConfig?.description}</p>
            </div>

            {/* Image Upload Area */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`relative aspect-video rounded-xl border-2 border-dashed transition-all cursor-pointer ${
                imagePreview 
                  ? "border-gold/50" 
                  : "border-slate-600 hover:border-gold/30"
              } bg-indigo-700/10 overflow-hidden`}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Selected"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <ImageIcon className="w-12 h-12 text-slate-600 mb-3" />
                  <p className="text-sm text-slate-400">Click to upload an image</p>
                  <p className="text-xs text-slate-600 mt-1">JPG, PNG, WebP (max 10MB)</p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                data-testid="vision-file-input"
              />
            </div>

            {/* Analyze Button */}
            <Button
              onClick={handleAnalyze}
              disabled={!selectedImage || analyzing}
              className="w-full bg-gold hover:bg-gold/90 text-indigo-700 font-bold py-3"
              data-testid="analyze-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing with Apollo...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Analyze Image
                </>
              )}
            </Button>

            {/* Sample images hint */}
            <p className="text-xs text-slate-500 text-center">
              Try uploading a kitchen photo, food dish, or equipment image
            </p>
          </div>

          {/* Results Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-inter font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-gold" />
              Analysis Results
            </h2>

            <AnimatePresence mode="wait">
              {analyzing && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-[#0A1244] rounded-xl border border-gold/20 p-6 text-center"
                >
                  <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto mb-3" />
                  <p className="text-slate-400">Apollo is analyzing your image...</p>
                </motion.div>
              )}

              {!analyzing && !results && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-[#0A1244] rounded-xl border border-slate-700/50 p-6 text-center"
                >
                  <Eye className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500">Upload an image to see analysis results</p>
                </motion.div>
              )}

              {results && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* Summary */}
                  <div className="bg-[#0A1244] rounded-xl border border-gold/30 p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <TypeIcon className={`w-5 h-5 text-${TypeConfig?.color}-400`} />
                        <h3 className="font-inter font-bold text-white">{TypeConfig?.name}</h3>
                      </div>
                      <button
                        onClick={() => handleSpeak(results.summary || "Analysis complete")}
                        disabled={isSpeaking}
                        className="p-2 rounded-lg hover:bg-gold/10 transition-colors"
                      >
                        <Volume2 className={`w-4 h-4 ${isSpeaking ? "text-gold animate-pulse" : "text-slate-500"}`} />
                      </button>
                    </div>
                    <p className="text-sm text-slate-300">{results.summary || "Analysis completed successfully."}</p>
                  </div>

                  {/* Alerts */}
                  {results.alerts && results.alerts.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-slate-400">Alerts</h4>
                      {results.alerts.map((alert, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.1 }}
                          className={`rounded-lg border p-4 ${SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.INFO}`}
                        >
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{alert.category}</p>
                              <p className="text-xs opacity-80 mt-1">{alert.description}</p>
                              {alert.recommended_action && (
                                <p className="text-xs mt-2 flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  {alert.recommended_action}
                                </p>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* No issues */}
                  {(!results.alerts || results.alerts.length === 0) && (
                    <div className="bg-lime/10 border border-lime/30 rounded-lg p-4 flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-lime" />
                      <div>
                        <p className="text-sm font-medium text-lime">All Clear!</p>
                        <p className="text-xs text-slate-400">No issues detected in this image.</p>
                      </div>
                    </div>
                  )}

                  {/* Raw metadata (collapsible) */}
                  {results.raw_metadata && (
                    <details className="bg-slate-800/30 rounded-lg border border-slate-700/50">
                      <summary className="px-4 py-3 text-sm text-slate-400 cursor-pointer hover:text-slate-300">
                        View Raw Analysis Data
                      </summary>
                      <pre className="px-4 pb-4 text-xs text-slate-500 overflow-auto max-h-48">
                        {JSON.stringify(results.raw_metadata, null, 2)}
                      </pre>
                    </details>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
}
